import { flow, makeObservable, observable } from 'mobx';
import RootStore from '.';
import BaseStore from './BaseStore';
import { ProfileApi } from '@shared/apis';
import publicationApi from '@shared/apis/PublicationApi';
import favoriteApi from '@shared/apis/FavoriteApi';
import { PagingResponse, ResponseData } from '@shared/types/http';
import { Profile, UserPublication } from '@shared/types/profile';
import { PUBLICATION_COLLECTION_TYPE, PUBLICATION_TYPE, PublicationType } from '@shared/types/enums';

export default class ProfileStore extends BaseStore {
    profile: Profile | null = null;
    api: ProfileApi;
    purchasedBooks: UserPublication[] = [];
    purchasedMagazines: UserPublication[] = [];
    totalPurchasedBooks: number = 0;
    totalPurchasedMagazines: number = 0;
    favoriteBooks: UserPublication[] = [];
    favoriteMagazines: UserPublication[] = [];
    totalFavoriteBooks: number = 0;
    totalFavoriteMagazines: number = 0;
    readingHistoryBooks: UserPublication[] = [];
    readingHistoryMagazines: UserPublication[] = [];
    totalReadingHistoryBooks: number = 0;
    totalReadingHistoryMagazines: number = 0;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            profile: observable,
            purchasedBooks: observable,
            purchasedMagazines: observable,
            totalPurchasedBooks: observable,
            totalPurchasedMagazines: observable,
            favoriteBooks: observable,
            favoriteMagazines: observable,
            totalFavoriteBooks: observable,
            totalFavoriteMagazines: observable,
            readingHistoryBooks: observable,
            readingHistoryMagazines: observable,
            totalReadingHistoryBooks: observable,
            totalReadingHistoryMagazines: observable,
            getUserProfile: flow.bound,
            updateGroupAdminProfile: flow.bound,
            fetchMyPublications: flow.bound,
            fetchMyFavorites: flow.bound,
            fetchReadingHistory: flow.bound,
            toggleFavorite: flow.bound
        });
        this.api = new ProfileApi();
    }

    *getUserProfile() {
        try {
            const res: ResponseData<Profile> = yield this.rootStore.apiStore.call(this.api, this.api.getUserProfile);
            if (res?.ok) {
                this.profile = res.data;
                return res.data;
            }
            return null;
        } catch (error) {
            console.error('Error getUserProfile in ProfileStore:', error);
            return null;
        }
    }

    *updateGroupAdminProfile(payload: any) {
        try {
            const res: ResponseData<Profile> = yield this.rootStore.apiStore.call(
                this.api,
                () => this.api.updateGroupAdminProfile(payload)
            );
            if (res?.ok) {
                this.profile = res.data;
                return res.data;
            }
            return null;
        } catch (error) {
            console.error('Error updateGroupAdminProfile in ProfileStore:', error);
            return null;
        }
    }

    *fetchMyPublications(publicationType?: PublicationType, page: number = 1, search?: string, size: number = 12, typeSearch?: string) {
        try {
            const res: ResponseData<PagingResponse<UserPublication>> = yield this.rootStore.apiStore.call(
                publicationApi,
                () => publicationApi.getUserPublications({ publicationType, collectionType: PUBLICATION_COLLECTION_TYPE.BOOKSHELF, size, page, search, typeSearch })
            );

            if (res?.ok && res.data && res.data?.elements) {
                const { elements, pagination } = res.data;
                if (publicationType === PUBLICATION_TYPE.Book) {
                    this.purchasedBooks = elements;
                    this.totalPurchasedBooks = pagination?.totalRecord || elements.length;
                } else if (publicationType === PUBLICATION_TYPE.MagazineIssue) {
                    this.purchasedMagazines = elements;
                    this.totalPurchasedMagazines = pagination?.totalRecord || elements.length;
                }
                return res.data;
            }
            return null;
        } catch (error) {
            console.error('Error fetchMyPublications in ProfileStore:', error);
            return null;
        }
    }

    *fetchMyFavorites(publicationType?: PublicationType, page: number = 1, search?: string, size: number = 12, typeSearch?: string) {
        try {
            const res: ResponseData<PagingResponse<UserPublication>> = yield this.rootStore.apiStore.call(
                publicationApi,
                () => publicationApi.getUserPublications({ publicationType, collectionType: PUBLICATION_COLLECTION_TYPE.FAVORITE, size, page, search, typeSearch })
            );

            if (res?.ok && res.data && res.data?.elements) {
                const { elements, pagination } = res.data;
                if (publicationType === PUBLICATION_TYPE.Book) {
                    this.favoriteBooks = elements;
                    this.totalFavoriteBooks = pagination?.totalRecord || elements.length;
                } else if (publicationType === PUBLICATION_TYPE.MagazineIssue) {
                    this.favoriteMagazines = elements;
                    this.totalFavoriteMagazines = pagination?.totalRecord || elements.length;
                }
                return res.data;
            }
            return null;
        } catch (error) {
            console.error('Error fetchMyFavorites in ProfileStore:', error);
            return null;
        }
    }

    *fetchReadingHistory(publicationType?: PublicationType, page: number = 1, search?: string, size: number = 12, typeSearch?: string) {
        try {
            const res: ResponseData<PagingResponse<UserPublication>> = yield this.rootStore.apiStore.call(
                publicationApi,
                () => publicationApi.getUserPublications({ publicationType, collectionType: PUBLICATION_COLLECTION_TYPE.HISTORY, size, page, search, typeSearch })
            );

            if (res?.ok && res.data && res.data?.elements) {
                const { elements, pagination } = res.data;
                if (publicationType === PUBLICATION_TYPE.Book) {
                    this.readingHistoryBooks = elements;
                    this.totalReadingHistoryBooks = pagination?.totalRecord || elements.length;
                } else if (publicationType === PUBLICATION_TYPE.MagazineIssue) {
                    this.readingHistoryMagazines = elements;
                    this.totalReadingHistoryMagazines = pagination?.totalRecord || elements.length;
                }
                return res.data;
            }
            return null;
        } catch (error) {
            console.error('Error fetchReadingHistory in ProfileStore:', error);
            return null;
        }
    }

    *toggleFavorite(publicationId: string | number, type: 'add' | 'remove') {
        try {
            // Call the corresponding API based on type
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(
                favoriteApi,
                () => type === 'add' ?
                    favoriteApi.addFavorite({ publicationId }) :
                    favoriteApi.removeFavorite(publicationId)
            );

            return res?.ok || false;
        } catch (error) {
            console.error('Error toggleFavorite in ProfileStore:', error);
            return false;
        }
    }

    clean() {
        super.clean();
        this.profile = null;
        this.purchasedBooks = [];
        this.purchasedMagazines = [];
        this.totalPurchasedBooks = 0;
        this.totalPurchasedMagazines = 0;
        this.favoriteBooks = [];
        this.favoriteMagazines = [];
        this.totalFavoriteBooks = 0;
        this.totalFavoriteMagazines = 0;
        this.readingHistoryBooks = [];
        this.readingHistoryMagazines = [];
        this.totalReadingHistoryBooks = 0;
        this.totalReadingHistoryMagazines = 0;
    }
}
